import type { OtterfolioPosition } from "../../lib/otterfolio";
import { PositionTileSparkline } from "./PositionTileSparkline";
import { RenditebrueckeBadge } from "./RenditebrueckeBadge";
import { OTTERFOLIO_THEME } from "./theme";

function fmtEur(cents: number): string {
  return new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(cents / 100);
}

function fmtPct(percent: number, digits = 1): string {
  const sign = percent >= 0 ? "+" : "";
  return `${sign}${percent.toFixed(digits)} %`;
}

function fmtSharePct(pct: number, digits = 1): string {
  return `${pct.toFixed(digits)} %`;
}

function fmtShares(n: number): string {
  // Bruchteile werden auf 4 Nachkommastellen gerundet (praezise genug fuer
  // Sparplaene wie 1,342131), ganze Stueck bleiben ohne Dezimalen.
  if (Number.isInteger(n)) return n.toLocaleString("de-DE");
  return n.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 4 });
}

interface Props {
  position: OtterfolioPosition;
  groupColor: string;
  groupLabel: string;
  onClick?: (position: OtterfolioPosition) => void;
}

export function PositionTile({ position, groupColor, groupLabel, onClick }: Props) {
  const perfPositive = position.returnPercent >= 0;
  const perfColor = perfPositive ? OTTERFOLIO_THEME.primary : OTTERFOLIO_THEME.highlight;
  const depotSharePct = position.sharePercent;
  const groupSharePct = position.groupSharePercent;

  return (
    <button
      type="button"
      onClick={() => onClick?.(position)}
      style={{
        background: OTTERFOLIO_THEME.cardBg,
        border: `1px solid ${OTTERFOLIO_THEME.cardBorder}`,
        borderRadius: 10,
        padding: 12,
        cursor: onClick ? "pointer" : "default",
        color: OTTERFOLIO_THEME.text,
        textAlign: "left",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 0,
      }}
    >
      <div style={{ fontWeight: 700, fontSize: 13 }}>{position.name}</div>
      {position.isin && (
        <div style={{ fontSize: 10, color: OTTERFOLIO_THEME.textMuted, marginTop: 1 }}>
          {position.isin}
        </div>
      )}
      <div style={{ fontSize: 15, fontWeight: 700, marginTop: 8 }}>
        {fmtEur(position.valueCents)}
      </div>
      <div style={{ fontSize: 12, fontWeight: 700, color: perfColor, marginTop: 2 }}>
        {fmtPct(position.returnPercent * 100)}
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          fontSize: 10.5,
          color: OTTERFOLIO_THEME.textMuted,
          marginTop: 4,
        }}
      >
        <span>Ø {fmtEur(position.avgCostCents)}</span>
        <span style={{ color: OTTERFOLIO_THEME.text, fontWeight: 600 }}>
          {fmtEur(position.currentPriceCents)}
        </span>
      </div>
      <div
        style={{
          fontSize: 10,
          color: OTTERFOLIO_THEME.textMuted,
          marginTop: 2,
        }}
      >
        {fmtShares(position.shares)} Stück
      </div>
      <div style={{ marginTop: 8 }}>
        <PositionTileSparkline positionId={position.id} trend={perfPositive ? "pos" : "neg"} />
      </div>
      <div style={{ marginTop: 6 }}>
        <RenditebrueckeBadge rb={position.renditebruecke} />
      </div>
      <div
        style={{
          marginTop: 8,
          display: "flex",
          flexDirection: "column",
          gap: 4,
          fontSize: 9.5,
          color: OTTERFOLIO_THEME.textMuted,
        }}
      >
        <ShareBar label="Depot" pct={depotSharePct} color={OTTERFOLIO_THEME.primary} />
        <ShareBar label={groupLabel} pct={groupSharePct} color={groupColor} />
      </div>
    </button>
  );
}

interface ShareBarProps {
  label: string;
  pct: number;
  color: string;
}

function ShareBar({ label, pct, color }: ShareBarProps) {
  const clamped = Math.min(100, Math.max(0, pct));
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <span style={{ minWidth: 48 }}>{label}</span>
      <div
        style={{
          flex: 1,
          height: 3,
          background: OTTERFOLIO_THEME.cardBorder,
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        <span
          style={{
            display: "block",
            height: "100%",
            width: `${clamped}%`,
            background: color,
            borderRadius: 2,
          }}
        />
      </div>
      <span
        style={{
          minWidth: 44,
          textAlign: "right",
          color: OTTERFOLIO_THEME.text,
          fontWeight: 700,
        }}
      >
        {fmtSharePct(pct)}
      </span>
    </div>
  );
}
